import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import isEmpty from 'lodash/isEmpty';
import { GraphQLCampaign } from 'modules/campaigns/lib';
import {
  CAMPAIGN_ALERT_UPDATES_DISABLED_STATES,
  CAMPAIGN_SETTINGS_EDITABLE_STATES,
  OPTIONAL_CAMPAIGN_STEPS,
} from 'modules/campaigns/lib/constants';
import {
  getIsScheduledCampaignType,
  getIsTriggeredCampaignType,
} from 'modules/campaigns/lib/utils';
import {
  AudienceList,
  CampaignRateLimit,
  CampaignState,
  CampaignType,
  Conversion,
  Experiment,
  PriorityLevel,
  QuietHours,
} from 'modules/domains/campaign';
import { MESSAGE_FORMAT, MessageMedium } from 'modules/graphql';
import { DisplayPriorityCampaign } from 'modules/placements/types';
import { TemplateType } from 'modules/templateEditor/common';
import { getLabelByMedium } from 'utils/messageMediums';
import { formatNumberAsOrdinal } from 'utils/numberHelpers';
import {
  CampaignStep,
  EmailPrimaryContent,
  EmbeddedPrimaryContent,
  InAppPrimaryContent,
  PrimaryContent,
  PushPrimaryContent,
  SMSPrimaryContent,
  WebPushPrimaryContent,
  WhatsAppPrimaryContent,
} from '../types';

export const getTemplateHasContent = (medium: MessageMedium, mediumData: PrimaryContent = {}) => {
  switch (medium) {
    case MessageMedium.Email:
      return (
        !!(mediumData as EmailPrimaryContent)?.html?.trim() ||
        !!(mediumData as EmailPrimaryContent)?.beeBuilderMetadata?.trim()
      );
    case MessageMedium.InApp:
      return (
        !!(mediumData as InAppPrimaryContent)?.html?.trim() ||
        !!(mediumData as InAppPrimaryContent)?.beeBuilderMetadata?.trim() ||
        (mediumData as InAppPrimaryContent)?.messageFormat === MESSAGE_FORMAT.Json
      );
    case MessageMedium.Push:
      return (
        !!(mediumData as PushPrimaryContent)?.message?.trim() ||
        !!(mediumData as PushPrimaryContent)?.isSilentPush
      );
    case MessageMedium.SMS: {
      const smsMediumData = mediumData as SMSPrimaryContent;
      return !!smsMediumData.message?.trim() || !!smsMediumData.verifiedContactCardId;
    }
    case MessageMedium.WebPush:
      return (
        !!(mediumData as WebPushPrimaryContent)?.message?.trim() ||
        !!(mediumData as WebPushPrimaryContent)?.title?.trim()
      );
    case EmbeddedMessage:
      return (
        !!(mediumData as EmbeddedPrimaryContent)?.body?.trim() ||
        !!(mediumData as EmbeddedPrimaryContent)?.title?.trim() ||
        !!(mediumData as EmbeddedPrimaryContent)?.elements?.mediaUrl?.trim() ||
        !!(mediumData as EmbeddedPrimaryContent)?.elements?.buttons?.length ||
        !!(mediumData as EmbeddedPrimaryContent)?.elements?.text?.filter((text) => !!text?.text)
          ?.length ||
        !!(
          (mediumData as EmbeddedPrimaryContent)?.payload &&
          (mediumData as EmbeddedPrimaryContent)?.payload !== '{}'
        )
      );
    case WhatsApp:
      return (
        !!(mediumData as WhatsAppPrimaryContent)?.header ||
        !!(mediumData as WhatsAppPrimaryContent)?.message?.trim() ||
        !!(mediumData as WhatsAppPrimaryContent)?.buttons?.length ||
        !!(mediumData as WhatsAppPrimaryContent)?.footer?.trim()
      );
    default:
      return true; // always show the preview if we don't identify the messageMedium
  }
};

export const getHasList = (lists: AudienceList[]) => !!lists?.length;

export const getScheduledCampaignTypeHasNoLists = (campaign: GraphQLCampaign) =>
  getIsScheduledCampaignType(campaign?.campaignTypeDefinition) && !getHasList(campaign?.lists);

export const getHasConversions = (conversions: Conversion[]) => conversions?.[0]?.events.length > 0;

export const getHasExperiments = (experiments: Experiment[]) => !!experiments?.length;

export const getHasQuietHours = (quietHours: QuietHours) => !isEmpty(quietHours);

export const getHasPriorityLevel = (priorityLevel: PriorityLevel) => !!priorityLevel;

export const getHasCampaignRateLimit = (campaignRateLimit: CampaignRateLimit) =>
  !!campaignRateLimit?.useRateLimit;

export const getTooltipContent = (action: string, messageMedium: MessageMedium) => {
  const medium = getLabelByMedium(messageMedium);
  return `You do not have permission to ${action} campaigns on this channel (${medium})`;
};

export const getHasDisplayPriority = (displayPriority?: number) =>
  typeof displayPriority === 'number' && displayPriority > 0;

export const getCurrentOrEditedOrder = (
  campaignDisplayPriorityOrder: DisplayPriorityCampaign[] = [],
  campaignId?: number,
) => {
  const currentOrEditedOrder =
    campaignDisplayPriorityOrder.findIndex((c) => c.id === campaignId) + 1;
  const campaignsLength = campaignDisplayPriorityOrder.length;
  return `${formatNumberAsOrdinal(currentOrEditedOrder)} of ${campaignsLength}`;
};

/**
 * Returns whether a given `campaignStep` is optional
 */
export const getIsCampaignStepOptional = (campaignStep: CampaignStep) =>
  OPTIONAL_CAMPAIGN_STEPS.includes(campaignStep);

/**
 * Returns whether a campaign type is blast
 */
export const getIsBlastCampaign = (campaignTypeDefinition: CampaignType) =>
  campaignTypeDefinition === CampaignType.Blast;

export const getIsEligibilityCampaign = (campaignTypeDefinition: CampaignType) =>
  campaignTypeDefinition === CampaignType.Eligibility;

export const getShouldDisableEditCampaignSettings = (campaignState: CampaignState) =>
  !CAMPAIGN_SETTINGS_EDITABLE_STATES.includes(campaignState);

export const getAreAlertUpdatesSupported = (
  campaignType: CampaignType,
  campaignState: CampaignState,
) => {
  const isTriggered = getIsTriggeredCampaignType(campaignType);

  const isFinishedTriggeredCampaign = isTriggered && campaignState === CampaignState.Finished; // Finished triggered campaigns can be updated
  const isFinishedNonTriggeredCampaign = !isTriggered && campaignState === CampaignState.Finished; // Finished non-triggered campaigns cannot be updated

  return (
    isFinishedTriggeredCampaign ||
    (!isFinishedNonTriggeredCampaign &&
      !CAMPAIGN_ALERT_UPDATES_DISABLED_STATES.includes(campaignState))
  );
};

/**
 * @param messageMedium
 * @param templateType
 * @returns boolean, true if the the campaign is freshly created and
 * does not yet have an actual valid WhatsApp template with content
 *
 * We differentiate a "default" template using the template type, as
 * its value will be null instead of one of the actual types,
 * business-initiated or user-initiated
 */
export const getIsDefaultWhatsAppTemplate = (
  messageMedium: MessageMedium,
  templateType: TemplateType,
): boolean => messageMedium === WhatsApp && !templateType;
